import type { Component } from "vue";

import { AmazonIcon, ClaudeIcon, CohereIcon, DartAiIcon, GeminiIcon, MetaIcon, OpenAiIcon, XaiIcon } from "~/icons";
import { AiModel, AiModelWithoutAuto, AiProvider, AiThinkingLevel } from "~/shared/enums";
import type { ValidationFunction } from "~/shared/types";
import { makeValidateRegex } from "~/utils/validation";

import { normalizeChatAttachmentMediaType } from "./mimeType";

const OPENAI_SUPPORTED_ATTACHMENT_MEDIA_TYPES = new Set([
  // spell-checker: disable
  "application/csv",
  "application/graphql",
  "application/javascript",
  "application/json",
  "application/json5",
  "application/msword",
  "application/pdf",
  "application/rtf",
  "application/toml",
  "application/typescript",
  "application/vnd.apple.iwork",
  "application/vnd.apple.keynote",
  "application/vnd.apple.pages",
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/x-awk",
  "application/x-bash",
  "application/x-graphql",
  "application/x-httpd-php-source",
  "application/x-httpd-php",
  "application/x-iif",
  "application/x-json5",
  "application/x-ndjson",
  "application/x-patch",
  "application/x-php",
  "application/x-powershell",
  "application/x-protobuf",
  "application/x-rust",
  "application/x-scala",
  "application/x-sql",
  "application/x-subrip",
  "application/x-terraform",
  "application/x-toml",
  "application/x-yaml",
  "application/yaml",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "message/rfc822",
  "text/calendar",
  "text/csv",
  "text/css",
  "text/html",
  "text/javascript",
  "text/jsx",
  "text/markdown",
  "text/plain",
  "text/rtf",
  "text/srt",
  "text/tsv",
  "text/tsx",
  "text/vbscript",
  "text/vtt",
  "text/x-R",
  "text/x-asm",
  "text/x-astro",
  "text/x-awk",
  "text/x-bash",
  "text/x-c",
  "text/x-c++",
  "text/x-clojure",
  "text/x-cmake",
  "text/x-csharp",
  "text/x-dart",
  "text/x-diff",
  "text/x-dockerfile",
  "text/x-ejs",
  "text/x-elixir",
  "text/x-erb",
  "text/x-erlang",
  "text/x-go",
  "text/x-golang",
  "text/x-gradle",
  "text/x-graphql",
  "text/x-groovy",
  "text/x-handlebars",
  "text/x-haskell",
  "text/x-hcl",
  "text/x-iif",
  "text/x-ini",
  "text/x-jade",
  "text/x-java",
  "text/x-jinja2",
  "text/x-julia",
  "text/x-kotlin",
  "text/x-less",
  "text/x-liquid",
  "text/x-lisp",
  "text/x-lua",
  "text/x-makefile",
  "text/x-mustache",
  "text/x-objectivec",
  "text/x-objectivec++",
  "text/x-patch",
  "text/x-perl",
  "text/x-php",
  "text/x-properties",
  "text/x-protobuf",
  "text/x-pug",
  "text/x-python",
  "text/x-r",
  "text/x-rst",
  "text/x-ruby",
  "text/x-rust",
  "text/x-sass",
  "text/x-scala",
  "text/x-scss",
  "text/x-script.python",
  "text/x-sh",
  "text/x-shellscript",
  "text/x-sql",
  "text/x-subrip",
  "text/x-swift",
  "text/x-terraform",
  "text/x-tex",
  "text/x-tmpl",
  "text/x-toml",
  "text/x-twig",
  "text/x-typescript",
  "text/x-vcard",
  "text/x-yaml",
  "text/x-zsh",
  "text/xml",
  // spell-checker: enable
]);
const OPENAI_BLACKLISTED_MEDIA_TYPES = new Set(["image/svg+xml"]);
const ANTHROPIC_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
]);
const GOOGLE_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES = new Set([
  // spell-checker: disable
  "application/json",
  "application/pdf",
  "audio/aac",
  "audio/aiff",
  "audio/flac",
  "audio/mp3",
  "audio/ogg",
  "audio/wav",
  "image/heic",
  "image/heif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/css",
  "text/csv",
  "text/html",
  "text/javascript",
  "text/plain",
  "text/rtf",
  "text/xml",
  "video/3gpp",
  "video/avi",
  "video/mp4",
  "video/mpeg",
  "video/mov",
  "video/mpg",
  "video/quicktime",
  "video/webm",
  "video/wmv",
  "video/x-flv",
  // spell-checker: enable
]);
const XAI_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES = new Set(["image/jpeg", "image/png", "text/plain"]);
const META_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES = new Set([
  "application/pdf",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/plain",
]);
const AMAZON_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/csv",
  "text/html",
  "text/markdown",
  "text/plain",
]);
const COHERE_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES = new Set(["text/plain"]);
const EMPTY_BLACKLISTED_MEDIA_TYPES = new Set<string>();

type ProviderConfig = {
  displayName: string;
  icon: Component;
  apiKeyUrl: string;
  apiKeyRegex: RegExp;
  supportedAttachmentMediaTypes: Set<string>;
  blacklistedAttachmentMediaTypes: Set<string>;
};

const PROVIDER_CONFIGS: Record<AiProvider, ProviderConfig> = {
  [AiProvider.OPENAI]: {
    displayName: "OpenAI",
    icon: OpenAiIcon,
    apiKeyUrl: "https://platform.openai.com/api-keys",
    apiKeyRegex: /^sk-[A-Za-z0-9_-]{20,}$/,
    supportedAttachmentMediaTypes: OPENAI_SUPPORTED_ATTACHMENT_MEDIA_TYPES,
    blacklistedAttachmentMediaTypes: OPENAI_BLACKLISTED_MEDIA_TYPES,
  },
  [AiProvider.ANTHROPIC]: {
    displayName: "Anthropic",
    icon: ClaudeIcon,
    apiKeyUrl: "https://platform.claude.com/settings/keys",
    apiKeyRegex: /^sk-ant-[A-Za-z0-9_-]{20,}$/,
    supportedAttachmentMediaTypes: ANTHROPIC_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES,
    blacklistedAttachmentMediaTypes: EMPTY_BLACKLISTED_MEDIA_TYPES,
  },
  [AiProvider.GOOGLE]: {
    displayName: "Google",
    icon: GeminiIcon,
    apiKeyUrl: "https://aistudio.google.com/apikey",
    apiKeyRegex: /^AIza[A-Za-z0-9_-]{35}$/,
    supportedAttachmentMediaTypes: GOOGLE_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES,
    blacklistedAttachmentMediaTypes: EMPTY_BLACKLISTED_MEDIA_TYPES,
  },
  [AiProvider.XAI]: {
    displayName: "xAI",
    icon: XaiIcon,
    apiKeyUrl: "https://console.x.ai/team/default/api-keys",
    apiKeyRegex: /^xai-[A-Za-z0-9_-]{20,}$/,
    supportedAttachmentMediaTypes: XAI_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES,
    blacklistedAttachmentMediaTypes: EMPTY_BLACKLISTED_MEDIA_TYPES,
  },
  [AiProvider.META]: {
    displayName: "Meta",
    icon: MetaIcon,
    apiKeyUrl: "https://llama.developer.meta.com/api-keys",
    apiKeyRegex: /^\S{20,}$/,
    supportedAttachmentMediaTypes: META_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES,
    blacklistedAttachmentMediaTypes: EMPTY_BLACKLISTED_MEDIA_TYPES,
  },
  [AiProvider.AMAZON]: {
    displayName: "Amazon",
    icon: AmazonIcon,
    apiKeyUrl: "https://console.aws.amazon.com/bedrock/home#/api-keys",
    apiKeyRegex: /^(ABSK[A-Za-z0-9+/=]{20,}|bedrock-api-key-[A-Za-z0-9+/=_-]{20,})$/,
    supportedAttachmentMediaTypes: AMAZON_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES,
    blacklistedAttachmentMediaTypes: EMPTY_BLACKLISTED_MEDIA_TYPES,
  },
  [AiProvider.COHERE]: {
    displayName: "Cohere",
    icon: CohereIcon,
    apiKeyUrl: "https://dashboard.cohere.com/api-keys",
    apiKeyRegex: /^[A-Za-z0-9]{40}$/,
    supportedAttachmentMediaTypes: COHERE_SUPPORTED_CHAT_ATTACHMENT_MEDIA_TYPES,
    blacklistedAttachmentMediaTypes: EMPTY_BLACKLISTED_MEDIA_TYPES,
  },
};

export const ORDERED_PROVIDERS = Object.values(AiProvider);
export const PROVIDER_TO_DISPLAY_NAME = Object.fromEntries(
  ORDERED_PROVIDERS.map((provider) => [provider, PROVIDER_CONFIGS[provider].displayName])
) as Record<AiProvider, string>;
export const PROVIDER_TO_ICON = Object.fromEntries(
  ORDERED_PROVIDERS.map((provider) => [provider, PROVIDER_CONFIGS[provider].icon])
) as Record<AiProvider, Component>;
export const PROVIDER_TO_API_KEY_URL = Object.fromEntries(
  ORDERED_PROVIDERS.map((provider) => [provider, PROVIDER_CONFIGS[provider].apiKeyUrl])
) as Record<AiProvider, string>;
export const PROVIDER_TO_API_KEY_PLACEHOLDER = Object.fromEntries(
  ORDERED_PROVIDERS.map((provider) => [provider, `Your ${PROVIDER_CONFIGS[provider].displayName} API key`])
) as Record<AiProvider, string>;
export const PROVIDER_TO_VALIDATE_API_KEY = Object.fromEntries(
  ORDERED_PROVIDERS.map((provider) => [
    provider,
    makeValidateRegex(
      PROVIDER_CONFIGS[provider].apiKeyRegex,
      `Enter a valid ${PROVIDER_CONFIGS[provider].displayName} API key`
    ),
  ])
) as Record<AiProvider, ValidationFunction>;

const NONE_THINKING_LEVELS = [AiThinkingLevel.NONE];
const MID_THINKING_LEVELS = [AiThinkingLevel.LOW, AiThinkingLevel.MEDIUM, AiThinkingLevel.HIGH];
const MID_THINKING_LEVELS_WITH_NONE = [AiThinkingLevel.NONE, ...MID_THINKING_LEVELS];
const FULL_THINKING_LEVELS = [...MID_THINKING_LEVELS, AiThinkingLevel.XHIGH];
const FULL_THINKING_LEVELS_WITH_NONE = [AiThinkingLevel.NONE, ...FULL_THINKING_LEVELS];

type ModelConfig = {
  model: AiModel;
  provider: AiProvider;
  displayName: string;
  subtitle: string;
  thinkingLevels: AiThinkingLevel[];
};

const ORDERED_MODEL_CONFIGS: ModelConfig[] = [
  {
    model: AiModel.GPT_6_ASTRA,
    provider: AiProvider.OPENAI,
    displayName: "GPT-6 Astra",
    subtitle: "Best for the hardest work",
    thinkingLevels: FULL_THINKING_LEVELS,
  },
  {
    model: AiModel.GPT_6_1_TERRA,
    provider: AiProvider.OPENAI,
    displayName: "GPT-6.1 Terra",
    subtitle: "Advanced deep reasoning",
    thinkingLevels: FULL_THINKING_LEVELS_WITH_NONE,
  },
  {
    model: AiModel.GPT_6_LUNA,
    provider: AiProvider.OPENAI,
    displayName: "GPT-6 Luna",
    subtitle: "Fast default for everyday work",
    thinkingLevels: FULL_THINKING_LEVELS_WITH_NONE,
  },
  {
    model: AiModel.CLAUDE_FABLE_5_1,
    provider: AiProvider.ANTHROPIC,
    displayName: "Fable 5.1",
    subtitle: "Most capable for hard projects",
    thinkingLevels: FULL_THINKING_LEVELS,
  },
  {
    model: AiModel.CLAUDE_OPUS_5_5,
    provider: AiProvider.ANTHROPIC,
    displayName: "Opus 5.5",
    subtitle: "Advanced for complex work",
    thinkingLevels: FULL_THINKING_LEVELS,
  },
  {
    model: AiModel.CLAUDE_SONNET_5_5,
    provider: AiProvider.ANTHROPIC,
    displayName: "Sonnet 5.5",
    subtitle: "Fast for everyday work",
    thinkingLevels: FULL_THINKING_LEVELS,
  },
  {
    model: AiModel.GEMINI_4_ARGON,
    provider: AiProvider.GOOGLE,
    displayName: "Gemini 4 Argon",
    subtitle: "Deep reasoning for complex work",
    thinkingLevels: MID_THINKING_LEVELS,
  },
  {
    model: AiModel.GEMINI_3_8_FLASH,
    provider: AiProvider.GOOGLE,
    displayName: "Gemini 3.8 Flash",
    subtitle: "Fast for everyday work",
    thinkingLevels: MID_THINKING_LEVELS,
  },
  {
    model: AiModel.GEMINI_3_1_FLASH_LITE,
    provider: AiProvider.GOOGLE,
    displayName: "Gemini 3.1 Flash Lite",
    subtitle: "Fastest multimodal option",
    thinkingLevels: MID_THINKING_LEVELS_WITH_NONE,
  },
  {
    model: AiModel.GROK_4_7,
    provider: AiProvider.XAI,
    displayName: "Grok 4.7",
    subtitle: "Most capable for complex work",
    thinkingLevels: FULL_THINKING_LEVELS,
  },
  {
    model: AiModel.GROK_4_20,
    provider: AiProvider.XAI,
    displayName: "Grok 4.20",
    subtitle: "Fast for everyday work",
    thinkingLevels: NONE_THINKING_LEVELS,
  },
  {
    model: AiModel.MUSE_WONDER_1_0,
    provider: AiProvider.META,
    displayName: "Muse Wonder 1.0",
    subtitle: "Complex coding and long-running tasks",
    thinkingLevels: FULL_THINKING_LEVELS,
  },
  {
    model: AiModel.MUSE_SPARK_1_3,
    provider: AiProvider.META,
    displayName: "Muse Spark 1.3",
    subtitle: "Fast for everyday work",
    thinkingLevels: FULL_THINKING_LEVELS,
  },
  {
    model: AiModel.NOVA_PRO_1_0,
    provider: AiProvider.AMAZON,
    displayName: "Nova Pro 1.0",
    subtitle: "Advanced multimodal option",
    thinkingLevels: NONE_THINKING_LEVELS,
  },
  {
    model: AiModel.NOVA_2_LITE,
    provider: AiProvider.AMAZON,
    displayName: "Nova 2 Lite",
    subtitle: "Fastest for quick tasks",
    thinkingLevels: MID_THINKING_LEVELS_WITH_NONE,
  },
  {
    model: AiModel.COMMAND_A_PLUS,
    provider: AiProvider.COHERE,
    displayName: "Command A+",
    subtitle: "Most capable enterprise model",
    thinkingLevels: NONE_THINKING_LEVELS,
  },
  {
    model: AiModel.COMMAND_A,
    provider: AiProvider.COHERE,
    displayName: "Command A",
    subtitle: "Fast for everyday work",
    thinkingLevels: NONE_THINKING_LEVELS,
  },
];

export const AUTO_MODEL = AiModel.GPT_6_LUNA;
const AUTO_CONFIG = {
  displayName: "Auto",
  subtitle: "Dart's best AI",
  icon: DartAiIcon,
};

export const PROVIDER_TO_MODELS_ORDERED = Object.fromEntries(
  ORDERED_PROVIDERS.map((provider) => [
    provider,
    ORDERED_MODEL_CONFIGS.filter((c) => c.provider === provider).map((c) => c.model),
  ])
) as Record<AiProvider, AiModel[]>;
export const AVAILABLE_MODELS_ORDERED_NO_AUTO: AiModel[] = ORDERED_MODEL_CONFIGS.map((c) => c.model);

export const getModelsForProviders = (providers: string[]) =>
  ORDERED_MODEL_CONFIGS.filter((c) => providers.includes(c.provider)).map((c) => c.model);

const MODEL_TO_CONFIG = new Map<AiModel | AiModelWithoutAuto, ModelConfig>(
  ORDERED_MODEL_CONFIGS.map((c) => [c.model, c])
);
export const getResolvedModel = (model: AiModel) => (MODEL_TO_CONFIG.has(model) ? model : AiModel.AUTO);
const getProviderConfig = (model: AiModel | AiModelWithoutAuto): ProviderConfig | undefined => {
  const config = MODEL_TO_CONFIG.get(model) ?? MODEL_TO_CONFIG.get(AUTO_MODEL);
  return config ? PROVIDER_CONFIGS[config.provider] : undefined;
};

export const MODEL_TO_DISPLAY_NAME = new Map<AiModel, string>([
  [AiModel.AUTO, AUTO_CONFIG.displayName],
  ...ORDERED_MODEL_CONFIGS.map((c) => [c.model, c.displayName] as const),
]);
export const MODEL_TO_SUBTITLE = new Map<AiModel, string>([
  [AiModel.AUTO, AUTO_CONFIG.subtitle],
  ...ORDERED_MODEL_CONFIGS.map((c) => [c.model, c.subtitle] as const),
]);
export const MODEL_TO_ICON = new Map<AiModel, Component>([
  [AiModel.AUTO, AUTO_CONFIG.icon],
  ...ORDERED_MODEL_CONFIGS.map((c) => [c.model, PROVIDER_CONFIGS[c.provider].icon] as const),
]);

const AUTO_THINKING_LEVELS = MODEL_TO_CONFIG.get(AUTO_MODEL)?.thinkingLevels ?? [];
export const MODEL_TO_AVAILABLE_THINKING_LEVELS = new Map<AiModel, AiThinkingLevel[]>(
  Object.values(AiModel).map((model) => [model, MODEL_TO_CONFIG.get(model)?.thinkingLevels ?? AUTO_THINKING_LEVELS])
);

export const MODELS_SUPPORTING_THINKING = new Set(
  [...MODEL_TO_AVAILABLE_THINKING_LEVELS.entries()]
    .filter(([, levels]) => levels.some((level) => !NONE_THINKING_LEVELS.includes(level)))
    .map(([model]) => model)
);
const DEFAULT_CASCADING_THINKING_LEVELS = [AiThinkingLevel.LOW, AiThinkingLevel.NONE];
export const MODEL_TO_DEFAULT_THINKING_LEVEL = Object.fromEntries(
  [...MODEL_TO_AVAILABLE_THINKING_LEVELS.entries()].map(([model, levels]) => [
    model,
    DEFAULT_CASCADING_THINKING_LEVELS.find((level) => levels.includes(level)),
  ])
) as Record<AiModel, AiThinkingLevel>;

export const THINKING_LEVEL_TO_DISPLAY_NAME: Record<AiThinkingLevel, string> = {
  [AiThinkingLevel.NONE]: "None",
  [AiThinkingLevel.LOW]: "Low",
  [AiThinkingLevel.MEDIUM]: "Medium",
  [AiThinkingLevel.HIGH]: "High",
  [AiThinkingLevel.XHIGH]: "Extra high",
};

export const THINKING_LEVEL_TO_CHIP_SUMMARY: Record<AiThinkingLevel, string> = {
  [AiThinkingLevel.NONE]: "No",
  [AiThinkingLevel.LOW]: "Lo",
  [AiThinkingLevel.MEDIUM]: "Med",
  [AiThinkingLevel.HIGH]: "Hi",
  [AiThinkingLevel.XHIGH]: "XHi",
};

export const getThinkingLevelDisplayName = ({ level, model }: { level: AiThinkingLevel; model: AiModel }) => {
  if (model === AiModel.GEMINI_3_1_FLASH_LITE && level === AiThinkingLevel.NONE) {
    return "Minimal";
  }
  return THINKING_LEVEL_TO_DISPLAY_NAME[level];
};

export const getThinkingLevelMenuTitle = ({
  level,
  index,
  levels,
  model,
}: {
  level: AiThinkingLevel;
  index: number;
  levels: AiThinkingLevel[];
  model: AiModel;
}) => {
  const isDefaultLevel = level === MODEL_TO_DEFAULT_THINKING_LEVEL[model];
  let title = getThinkingLevelDisplayName({ level, model });
  if (index === 0) {
    title = `${title} (fastest)`;
  } else if (index === levels.length - 1) {
    title = `${title} (slowest)`;
  }
  if (isDefaultLevel) {
    title = `${title} (default)`;
  }
  return title;
};

export const getModelChipLabel = (model: AiModel, thinkingLevel?: AiThinkingLevel) => {
  const modelLabel = MODEL_TO_DISPLAY_NAME.get(model) ?? MODEL_TO_DISPLAY_NAME.get(AiModel.AUTO) ?? "";
  if (!thinkingLevel || !MODELS_SUPPORTING_THINKING.has(model)) {
    return modelLabel;
  }

  const defaultLevel = MODEL_TO_DEFAULT_THINKING_LEVEL[model];
  if (thinkingLevel === defaultLevel) {
    return modelLabel;
  }
  return `${modelLabel} / ${THINKING_LEVEL_TO_CHIP_SUMMARY[thinkingLevel]}`;
};

export const CHAT_ATTACHMENT_ACCEPTED_DATA_TYPES = new Set(
  Object.values(PROVIDER_CONFIGS).flatMap((p) => [...p.supportedAttachmentMediaTypes])
);

const isSupportedAttachmentMediaType = (supportedTypes: Set<string>, normalizedType: string) => {
  let isSupported = false;
  supportedTypes.forEach((supportedType) => {
    if (isSupported) {
      return;
    }
    if (
      supportedType === "*"
        ? true
        : supportedType.endsWith("/*")
          ? normalizedType.startsWith(supportedType.slice(0, -1))
          : normalizedType === supportedType
    ) {
      isSupported = true;
    }
  });
  return isSupported;
};

export const isAttachmentMediaTypeSupportedByModel = (model: AiModel | AiModelWithoutAuto, mediaType: string) => {
  const provider = getProviderConfig(model);
  if (!provider) {
    return false;
  }
  const normalizedMediaType = normalizeChatAttachmentMediaType(mediaType);
  if (provider.blacklistedAttachmentMediaTypes.has(normalizedMediaType)) {
    return false;
  }
  return isSupportedAttachmentMediaType(provider.supportedAttachmentMediaTypes, normalizedMediaType);
};

export const getModelsSupportingAttachmentMediaType = (mediaType: string) =>
  AVAILABLE_MODELS_ORDERED_NO_AUTO.filter((model) => isAttachmentMediaTypeSupportedByModel(model, mediaType));
